import React, { useState, useEffect } from "react";
import {
  Grid,
  Card,
  CardContent,
  Box,
  Typography,
  Button,
  Chip,
  Avatar,
  CircularProgress,
} from "@mui/material";
import { Person } from "@mui/icons-material";
import AssignMemoryBookModal from "./AssignMemoryBookModal";
import apiService from "../../../service/Api";
import { toast } from "react-toastify";

interface User {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string | null;
  profile_picture_url: string | null;
}

interface MemoryBookAssignment {
  id: number;
  stream: {
    id: number;
  };
  assigned_to: User;
  physical_comments: number | null;
  due_date: string;
  assigned_at: string;
}

interface MemoryBookCardProps {
  streamId?: number;
}

const MemoryBookCard: React.FC<MemoryBookCardProps> = ({ streamId }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [assignedTo, setAssignedTo] = useState<{
    id: number;
    name: string;
    email?: string;
    avatarUrl?: string;
  } | null>(null);
  const [collectedComments, setCollectedComments] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [assignmentId, setAssignmentId] = useState<number | null>(null);

  useEffect(() => {
    const fetchAssignment = async () => {
      if (!streamId) return;

      setLoading(true);
      try {
        const response = await apiService.get<{
          results: MemoryBookAssignment[];
        }>(`/api/streams/memory-book-assignment/?stream=${streamId}`);

        if (response.data.results && response.data.results.length > 0) {
          const assignment = response.data.results[0];

          setAssignmentId(assignment.id);
          setAssignedTo({
            id: assignment.assigned_to.id,
            name: `${assignment.assigned_to.first_name} ${assignment.assigned_to.last_name}`,
            email: assignment.assigned_to.email,
            avatarUrl: assignment.assigned_to.profile_picture_url || undefined,
          });
          setCollectedComments(String(assignment.physical_comments || ""));
        }
      } catch (error) {
        console.error("Failed to fetch assignment:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAssignment();
  }, [streamId]);

  const handleAssign = async (user: User, comments: string) => {
    if (!streamId) {
      toast.error("Stream ID is required");
      return;
    }

    try {
      const payload = {
        stream_id: streamId,
        assigned_to_id: user.id,
        physical_comments: parseInt(comments, 10),
      };

      let response;

      if (assignmentId) {
        response = await apiService.patch<MemoryBookAssignment>(
          `/api/streams/memory-book-assignment/${assignmentId}/`,
          payload,
        );
      } else {
        response = await apiService.post<MemoryBookAssignment>(
          `/api/streams/memory-book-assignment/`,
          payload,
        );
      }

      setAssignmentId(response.data.id);
      setAssignedTo({
        id: user.id,
        name: `${user.first_name} ${user.last_name}`,
        email: user.email,
        avatarUrl: user.profile_picture_url ?? undefined,
      });
      setCollectedComments(comments);

      toast.success(
        assignmentId
          ? "Memory book assignment updated successfully!"
          : "Memory book assigned successfully!",
      );
    } catch {
      toast.error("Failed to assign memory book. Please try again.");
    }
  };

  if (loading) {
    return (
      <Grid>
        <Card sx={{ boxShadow: 0, height: "auto", mt: 2 }}>
          <CardContent>
            <Box textAlign="center" py={2}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary" mt={1}>
                Loading assignment...
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    );
  }

  return (
    <>
      <Grid>
        <Card sx={{ boxShadow: 0, height: "auto", mt: 2 }}>
          <CardContent>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Assign Memory Book
              </Typography>
              <Button
                variant="contained"
                onClick={() => setModalOpen(true)}
                sx={{
                  textTransform: "none",
                }}
              >
                {assignedTo ? "Reassign" : "Assign Memory Book"}
              </Button>
            </Box>

            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                {assignedTo ? (
                  <Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Assigned to:
                      </Typography>
                      <Chip
                        avatar={
                          assignedTo.avatarUrl ? (
                            <Avatar src={assignedTo.avatarUrl} />
                          ) : (
                            <Avatar>
                              <Person fontSize="small" />
                            </Avatar>
                          )
                        }
                        label={assignedTo.name}
                        variant="outlined"
                        size="small"
                      />
                      {assignedTo.email && (
                        <Typography variant="caption" color="text.secondary">
                          ({assignedTo.email})
                        </Typography>
                      )}
                    </Box>
                    {collectedComments && (
                      <Typography variant="body2" color="text.secondary">
                        Collected Comments: {collectedComments}
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No memory book assigned yet.
                  </Typography>
                )}
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      <AssignMemoryBookModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onAssign={handleAssign}
        //streamId={streamId}
        currentAssignee={assignedTo}
        initialCollectedComments={collectedComments}
      />
    </>
  );
};

export default MemoryBookCard;
